import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在</p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const categoryName = categoryInfo ? categoryInfo.name : article.category;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '91自拍论坛',
      url: 'https://www.91selfie.com/',
    },
    url: `https://www.91selfie.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.91selfie.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <article>
            {/* Header */}
            <section className="relative w-full h-[320px] md:h-[440px] flex items-end overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 91自拍论坛`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
              <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-12 max-w-4xl mx-auto">
                <div className="mb-4">
                  <Breadcrumb
                    items={[
                      { label: categoryName, href: `/category/${article.category}` },
                      { label: article.title },
                    ]}
                  />
                </div>
              </div>
            </section>

            {/* Content */}
            <section className="py-10 md:py-14 bg-background-50">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                  >
                    {categoryName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-xs"></i>
                    </div>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-xs"></i>
                    </div>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs"></i>
                    </div>
                    阅读约{article.readTime}
                  </span>
                </div>

                <div
                  className="prose-custom text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Share */}
                <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                    aria-label="分享到微信"
                  >
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                    aria-label="分享到微博"
                  >
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                    aria-label="复制链接"
                  >
                    <i className="ri-link"></i>
                  </a>
                </div>
              </div>
            </section>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="py-14 md:py-20 bg-background-100">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                  <h2 className="font-heading text-2xl text-foreground-900 mb-8 text-center">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.id} article={ra} />
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}